import { app } from "@comact/crc";
import sprintf from "i18next-sprintf-postprocessor";
import React from "react";
import { Route } from "react-router";
import { Navigate } from "react-router-dom";
import { ajaxRequestDefaults, getSystemData, getVersionsInfos, PageContainer, RootContainer } from "./appInit";
import PageSalesforceCases from "./salesforce/components/PageSalesforceCases";

const PageDowntimeCausesEdit = React.lazy(() => import("./downtimes/settings/components/PageDowntimeCausesEdit"));
const PageOverview = React.lazy(() => import("./node/components/PageOverview"));
const PageDowntimeSettings = React.lazy(() => import("./downtimes/settings/components/PageDowntimeSettings"));
const PageDetails = React.lazy(() => import("./node/components/PageDetails"));
const PageLinksEdit = React.lazy(() => import("./node/components/PageLinksEdit"));
const PageKpiRegistrationEdit = React.lazy(() => import("./kpiRegistrations/components/PageKpiRegistrationEdit"));
const PageTemplatesList = React.lazy(() => import("./nodeTemplate/components/PageTemplatesList"));
const PageBackups = React.lazy(() => import("./backups/components/PageBackups"));
const PageApplicationLogs = React.lazy(() => import("./backups/components/PageApplicationLogs"));
const PageCmocBackupsRestore = React.lazy(() => import("./backups/components/PageCmocBackupsRestore"));
const PageLink = React.lazy(() => import("./links/components/PageLink"));
const PageDowntimes = React.lazy(() => import("./downtimes/components/PageDowntimes"));
const PageDowntimeEdit = React.lazy(() => import("./downtimes/components/PageDowntimeEdit"));
const PageWatchlist = React.lazy(() => import("./watchlist/components/PageWatchlist"));
const PageWatchlistItemEdit = React.lazy(() => import("./watchlist/components/PageWatchlistItemEdit"));

const PageUsers = React.lazy(() => import("./users/components/PageUsers"));
const PageUserEdit = React.lazy(() => import("./users/components/PageUserEdit"));
const PageMyPreferences = React.lazy(() => import("./users/components/PageMyPreferences"));
const PageGroups = React.lazy(() => import("./users/components/PageGroups"));
const PageGroupEdit = React.lazy(() => import("./users/components/PageGroupEdit"));

const PageRoughInventory = React.lazy(() => import("./roughInventory/components/PageRoughInventory"));
const PageRoughInventoryEdit = React.lazy(() => import("./roughInventory/components/PageRoughInventoryConfigurationEdit"));
const PageEditInventoryCounts = React.lazy(() => import("./roughInventory/components/PageEditInventoryCounts"));
const PageInventoryDiff = React.lazy(() => import("./roughInventory/components/PageInventoryDiff"));
const PageInventoryHistory = React.lazy(() => import("./roughInventory/components/PageInventoryHistory"));

const PagePackPiecesConfiguration = React.lazy(() => import("./pmPackForecasting/components/PagePackPiecesConfiguration"));
const PageFinishedProductsConfiguration = React.lazy(() => import("./pmPackForecasting/components/PageFinishedProductsConfiguration"));
const PageProjectionRun = React.lazy(() => import("./pmPackForecasting/components/PageProjectionRun"));
const PageVolumeForecast = React.lazy(() => import("./pmPackForecasting/components/PageVolumeForecast"));

const PageGrafanaAlerts = React.lazy(() => import("./notifications/grafanaAlerts/components/PageGrafanaAlerts"));
const PageGrafanaAlertEdit = React.lazy(() => import("./notifications/grafanaAlerts/components/PageGrafanaAlertEdit"));
const PageGrafanaIntegration = React.lazy(() => import("./grafanaIntegration/components/PageGrafanaIntegration"));
const PageNotificationGroups = React.lazy(() => import("./notifications/components/PageNotificationGroups"));
const PageNotificationGroupsEdit = React.lazy(() => import("./notifications/components/PageNotificationGroupEdit"));
const PageScheduledReports = React.lazy(() => import("./notifications/scheduledReports/components/PageScheduledReports"));
const PageFlaskReportEdit = React.lazy(() => import("./notifications/scheduledReports/components/PageFlaskReportEdit"));
const PageSummarizedReportEdit = React.lazy(() => import("./notifications/scheduledReports/components/PageSummarizedReportEdit"));
const PageThirdPartyIntegration = React.lazy(() => import("./thirdPartyIntegration/components/PageThirdPartyIntegration"));

const PageReportBrowser = React.lazy(() => import("./reports/browser/components/PageReportBrowser"));

const PageResourceLibrary = React.lazy(() => import("./resourceLibrary/components/PageResourceLibrary"));
const PageResourceLibraryTrainings = React.lazy(() => import("./resourceLibrary/components/PageResourceLibraryTrainings"));
const PageResourceLibraryUserManuals = React.lazy(() => import("./resourceLibrary/components/PageResourceLibraryUserManuals"));

const PageExternalProvider = React.lazy(() => import("./externalProvider/components/PageExternalProvider"));
const PageCustomForms = React.lazy(() => import("./customForms/components/PageCustomForms"));
const PageCustomForm = React.lazy(() => import("./customForms/components/PageCustomFormEntries"));
const PageCustomFormEdit = React.lazy(() => import("./customForms/components/PageCustomFormEdit"));

const PageCloudShiftEditDefinition = React.lazy(() => import("./shifts/cloudShifts/components/PageCloudShiftEditDefinition"));
const PageCloudShiftEditShift = React.lazy(() => import("./shifts/cloudShifts/components/PageCloudShiftEditShift"));
const PageCloudShifts = React.lazy(() => import("./shifts/cloudShifts/components/PageCloudShifts"));

const PageOdometers = React.lazy(() => import("./odometers/components/PageOdometers"));

const PageGrafanaDashboards = React.lazy(() => import("./embeddedGrafana/components/PageEmbeddedDashboards"));
const PageComponentsEngine = React.lazy(() => import("./componentsEngine/components/PageComponentsEngine"));

const PageCreateSalesForceTicket = React.lazy(() => import("./salesforce/components/PageCreateSalesForceTicket"));

const hamburgerMenu = React.lazy(() => import("./components/HamburgerMenu"));

const PageSystemEvents = React.lazy(() => import("./events/components/PageSystemEvents"));

// Initialize the application
// @ts-ignore
app.init({
    projectLocales: import.meta.glob("../i18n/*.json", { import: "default" }),
    rootContainer: RootContainer,
    mainTemplate: PageContainer,
    getSystemData,
    ajaxRequestDefaults,
    getVersionsInfos,
    menu: [],
    hamburgerMenu,
    routes: [
        // Overview
        <Route key="index" path="/" element={<PageOverview />} />,
        <Route key="perspective/portal-root" path="/portal" element={<PageOverview />} />,
        <Route key="perspective/overview-root" path="/overview" element={<PageOverview />} />,
        <Route key="perspective/overview" path="/overview/:nodeId" element={<PageOverview />} />,

        // Backups
        <Route key="perspective/backups" path="/details/backups/:backupsType/:nodeId" element={<PageBackups />} />,
        <Route key="perspective/backups" path="/details/backups/application-logs/:nodeId" element={<PageApplicationLogs />} />,

        // Details
        <Route key="perspective/details/root" path="/details" element={<PageDetails />} />,
        <Route key="perspective/details" path="/details/:nodeId" element={<PageDetails />} />,
        <Route key="perspective/details/edit/links" path="/details/:nodeId/edit/links" element={<PageLinksEdit />} />,
        <Route key="perspective/details/edit/kpi-registrations" path="/details/edit/KpiRegistrations/:nodeId/" element={<PageKpiRegistrationEdit />} />,
        // <Route key="perspective/details/open-camera-modal" path="/details/:nodeId/:action" element={<PageDetails />} />,

        // Users + login CMOC
        <Route key="user-management/users/redirect" path="/users" element={<Navigate to="/user-management/users" />} />, // Old path given by the service list of the control-center
        <Route key="user-management/users" path="/user-management/users" element={<PageUsers />} />,
        <Route key="user-management/users/edit" path="/user-management/users/edit/:userId" element={<PageUserEdit />} />,
        <Route key="perspective/my-preferences" path="/my-preferences" element={<PageMyPreferences />} />,
        <Route key="user-management/groups" path="/user-management/groups" element={<PageGroups />} />,
        <Route key="user-management/groups/edit" path="/user-management/groups/edit/:groupId" element={<PageGroupEdit />} />,

        // Templates (nodes)
        <Route key="perspective/templates" path="/templates" element={<PageTemplatesList />} />,

        // Cmoc backups restore from external CMOC (opened in a iframe inside the control-center)
        <Route key="cmoc-backups-restore" path="/cmoc-backups-restore" element={<PageCmocBackupsRestore />} />,

        // Dynamic links
        <Route key="perspective/links/linkID" path="/link/:nodeId/:linkId" element={<PageLink />} />,

        // Downtimes
        <Route key="perspective/events/downtime-app/downtimes" path="/events/downtime-app/downtimes/:role/:nodeId" element={<PageDowntimes />} />,
        <Route key="perspective/events/downtime-app/downtimes/edit" path="/events/downtime-app/downtimes/admin/edit/:nodeId/:id" element={<PageDowntimeEdit />} />,
        <Route key="perspective/events/downtime-app/settings" path="/events/downtime-app/settings/:nodeId" element={<PageDowntimeSettings />} />,
        <Route key="perspective/events/downtime-app/causes/edit" path="/events/downtime-app/causes/edit/:nodeId" element={<PageDowntimeCausesEdit />} />,

        // Watchlist
        <Route key="perspective/events/downtime-app/watchlist" path="/events/downtime-app/watchlist/:nodeId" element={<PageWatchlist />} />,
        <Route key="perspective/events/downtime-app/watchlist/edit" path="/events/downtime-app/watchlist/edit/:nodeId/:id" element={<PageWatchlistItemEdit />} />,

        // Organization Settings
        <Route key="perspective/organization-settings/external-provider" path="/organization-settings/external-provider/:nodeId" element={<PageExternalProvider />} />,
        <Route key="perspective/organization-settings/grafana-integration" path="/organization-settings/grafana-integration/:nodeId" element={<PageGrafanaIntegration />} />,
        <Route key="perspective/organization-settings/third-party-integration" path="/organization-settings/third-party-integration/:nodeId" element={<PageThirdPartyIntegration />} />,
        <Route key="perspective/organization-settings/external-provider" path="/organization-settings/external-provider" element={<PageExternalProvider />} />,
        <Route key="perspective/organization-settings/grafana-integration" path="/organization-settings/grafana-integration" element={<PageGrafanaIntegration />} />,
        <Route key="perspective/organization-settings/third-party-integration" path="/organization-settings/third-party-integration" element={<PageThirdPartyIntegration />} />,

        // RoughInventory
        <Route key="perspective/inventory/configuration" path="/inventory/configuration/:nodeId" element={<PageRoughInventory />} />,
        <Route key="perspective/inventory/configuration/edit" path="/inventory/configuration/edit/:nodeId" element={<PageRoughInventoryEdit />} />,
        <Route key="perspective/inventory/configuration/editCounts" path="/inventory/configuration/editCounts/:nodeId" element={<PageEditInventoryCounts />} />,
        <Route key="perspective/inventory/diff" path="/inventory/diff/:nodeId" element={<PageInventoryDiff />} />,
        <Route key="perspective/inventory/history" path="/inventory/history/:nodeId" element={<PageInventoryHistory />} />,

        // Planer mill pack forecasting
        <Route key="perspective/pmPackForecasting/configuration" path="/pmPackForecasting/configuration/:nodeId/:id" element={<PagePackPiecesConfiguration />} />,
        <Route
            key="perspective/pmPackForecasting/configuration/finishedProducts"
            path="/pmPackForecasting/configuration/:nodeId/finishedProductsConfiguration"
            element={<PageFinishedProductsConfiguration />}
        />,
        <Route key="perspective/pmPackForecasting/projectionRun" path="/pmPackForecasting/projectionRun/:nodeId" element={<PageProjectionRun />} />,
        <Route key="perspective/pmPackForecasting/volumeForecast" path="/pmPackForecasting/volumeForecast/:nodeId" element={<PageVolumeForecast />} />,

        // Notifications
        <Route key="perspective/notifications/grafana-alerts" path="/notifications/grafana-alerts/:nodeId" element={<PageGrafanaAlerts />} />,
        <Route key="perspective/notifications/grafana-alerts/edit" path="/notifications/grafana-alerts/:nodeId/edit/:id" element={<PageGrafanaAlertEdit />} />,
        <Route key="perspective/notifications/notification-groups" path="/notifications/notification-groups/:nodeId" element={<PageNotificationGroups />} />,
        <Route key="perspective/notifications/notification-groups/edit" path="/notifications/notification-groups/:nodeId/edit/:id" element={<PageNotificationGroupsEdit />} />,
        <Route key="perspective/notifications/scheduled-reports" path="/notifications/scheduled-reports/:nodeId" element={<PageScheduledReports />} />,
        <Route key="perspective/notifications/scheduled-reports/edit-flask" path="/notifications/scheduled-reports/:nodeId/edit-flask/:id" element={<PageFlaskReportEdit />} />,
        <Route key="perspective/notifications/scheduled-reports/edit-summarized" path="/notifications/scheduled-reports/:nodeId/edit-summarized/:id" element={<PageSummarizedReportEdit />} />,

        // Reports
        <Route key="perspective/reportBrowser" path="/reports/:nodeId" element={<PageReportBrowser />} />,

        // Resource Library
        <Route key="perspective/resource-library" path="/resource-library/:documentType/:nodeId" element={<PageResourceLibrary />} />,
        <Route key="perspective/resource-library/trainings" path="/resource-library/trainings/:nodeId" element={<PageResourceLibraryTrainings />} />,
        <Route key="perspective/resource-library/trainings" path="/resource-library/user-manuals/:nodeId" element={<PageResourceLibraryUserManuals />} />,

        // Custom Forms
        <Route key="perspective/custom-forms" path="/custom-forms/:nodeId" element={<PageCustomForms />} />,
        <Route key="perspective/custom-forms" path="/custom-forms/:nodeId/:formId" element={<PageCustomForm />} />,
        <Route key="perspective/custom-forms-edit" path="/custom-forms-edit/:nodeId/:formId" element={<PageCustomFormEdit />} />,

        <Route key="perspective/schedule" path="/schedule/:nodeId" element={<PageCloudShifts />} />,

        // Cloud Shifts Edit shift
        <Route key="perspective/cloud-shifts-edit/shift" path="/cloud-shifts-edit/:nodeId/shift/:shiftId" element={<PageCloudShiftEditShift />} />,
        <Route key="perspective/cloud-shifts-edit/shift-new" path="/cloud-shifts-edit/:nodeId/shift" element={<PageCloudShiftEditShift />} />,
        // Cloud Shifts Edit shift definition
        <Route key="perspective/cloud-shifts-edit/definition" path="/cloud-shifts-edit/:nodeId/definition/:defId" element={<PageCloudShiftEditDefinition />} />,
        // New shift or shiftDefinition
        <Route key="perspective/cloud-shifts-edit" path="/cloud-shifts-edit/:nodeId" element={<PageCloudShiftEditDefinition />} />,

        // Salesforce
        <Route key="perspective/customer/requests" path="/customer/requests/:nodeId" element={<PageSalesforceCases />} />,
        <Route key="perspective/customer/requests/create" path="/customer/requests/create/:nodeId" element={<PageCreateSalesForceTicket />} />,

        // Odometers
        <Route key="perspective/odometers" path="/odometers/:nodeId" element={<PageOdometers />} />,

        // Grafana Dashboards
        <Route key="perspective/dashboards" path="/dashboards/:nodeId" element={<PageGrafanaDashboards />} />,

        // Components Engine
        <Route key="perspective/components-engine" path="/components-engine/:nodeId" element={<PageComponentsEngine />} />,

        // Smart vision/events
        <Route key="perspective/events/timeline" path="/events/timeline/:nodeId" element={<PageSystemEvents />} />,
    ],
});

app.i18next.use(sprintf);
